import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Droplets, Clock, Flame } from "lucide-react";

const steps = [
  {
    icon: Droplets,
    title: "Rinse",
    description:
      "Gently wash the rice 2-3 times in cold water until the water runs clear. This removes excess starch for fluffy, separate grains.",
  },
  {
    icon: Clock,
    title: "Soak",
    description:
      "Soak the rice for 30-45 minutes in fresh water. This allows the grains to absorb moisture and expand to their full, majestic length.",
  },
  {
    icon: Flame,
    title: "Cook",
    description:
      "Use a 1:1.5 rice-to-water ratio. Bring to a boil, then reduce to the lowest heat. Cover and cook for 12-15 minutes. Let it rest for 5 minutes before serving.",
  },
];

const riceTypes = [
  {
    name: "Classic Basmati",
    waterRatio: "1 : 1.5",
    soakTime: "30 min",
    cookTime: "12 min",
  },
  {
    name: "Sella / Parboiled",
    waterRatio: "1 : 2",
    soakTime: "45 min",
    cookTime: "18 min",
  },
  {
    name: "Brown Basmati",
    waterRatio: "1 : 2.5",
    soakTime: "60 min",
    cookTime: "25 min",
  },
];

const RiceGuide = () => {
  const [open, setOpen] = useState(false);

  return (
    <section
      id="rice-guide"
      data-testid="rice-guide-section"
      className="relative py-24 md:py-32 bg-brand-bg-dark overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1762151532995-c2e6cb6449d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxjaGVmJTIwY29va2luZyUyMHByZW1pdW0lMjBmb29kfGVufDB8fHx8MTc3NjA2NDk3OHww&ixlib=rb-4.1.0&q=85"
          alt="Chef cooking"
          className="w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-brand-bg-dark/80" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center space-y-8">
        <p className="text-xs uppercase tracking-[0.3em] font-body font-semibold text-brand-gold">
          Master the Grain
        </p>
        <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight text-brand-text-light">
          The Perfect <span className="italic text-brand-gold">Basmati</span> Guide
        </h2>
        <p className="font-body text-base font-light leading-relaxed text-brand-text-light/60 max-w-2xl mx-auto">
          Unlock the full potential of every grain. Our master chefs share their
          secrets to cooking basmati rice that is aromatic, fluffy, and perfectly
          elongated every single time.
        </p>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="rice-guide-trigger"
              className="inline-flex items-center gap-3 px-10 py-4 border border-brand-gold text-brand-gold text-xs uppercase tracking-[0.2em] font-body font-semibold hover:bg-brand-gold hover:text-brand-primary transition-all duration-300"
            >
              Open Rice Guide
            </button>
          </DialogTrigger>
          <DialogContent
            data-testid="rice-guide-modal"
            className="max-w-2xl bg-brand-primary border border-brand-gold/20 text-brand-text-light p-0 overflow-hidden"
          >
            <DialogHeader className="p-8 pb-0">
              <DialogTitle className="font-heading text-3xl font-light text-brand-gold tracking-tight">
                The Sartaj Rice Guide
              </DialogTitle>
              <DialogDescription className="text-brand-text-light/60 font-body text-sm">
                Three simple steps to perfection
              </DialogDescription>
            </DialogHeader>

            <div className="p-8 space-y-8">
              {/* Steps */}
              <div className="grid gap-6">
                {steps.map((step, i) => (
                  <div key={i} className="flex gap-5">
                    <div className="flex-shrink-0 w-12 h-12 border border-brand-gold/30 flex items-center justify-center">
                      <step.icon size={20} className="text-brand-gold" />
                    </div>
                    <div className="space-y-1">
                      <h4 className="font-heading text-lg text-brand-gold-light">
                        {step.title}
                      </h4>
                      <p className="font-body text-sm font-light leading-relaxed text-brand-text-light/70">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Rice Type Table */}
              <div className="border-t border-brand-gold/20 pt-6">
                <h4 className="text-xs uppercase tracking-[0.2em] text-brand-gold font-body font-semibold mb-4">
                  Quick Reference
                </h4>
                <div className="grid grid-cols-4 gap-4 text-xs font-body">
                  <div className="text-brand-text-light/40 uppercase tracking-wider">
                    Type
                  </div>
                  <div className="text-brand-text-light/40 uppercase tracking-wider">
                    Water
                  </div>
                  <div className="text-brand-text-light/40 uppercase tracking-wider">
                    Soak
                  </div>
                  <div className="text-brand-text-light/40 uppercase tracking-wider">
                    Cook
                  </div>
                  {riceTypes.map((type, i) => (
                    <>
                      <div key={`name-${i}`} className="text-brand-text-light/80">
                        {type.name}
                      </div>
                      <div key={`water-${i}`} className="text-brand-gold">
                        {type.waterRatio}
                      </div>
                      <div key={`soak-${i}`} className="text-brand-text-light/70">
                        {type.soakTime}
                      </div>
                      <div key={`cook-${i}`} className="text-brand-text-light/70">
                        {type.cookTime}
                      </div>
                    </>
                  ))}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default RiceGuide;
